import { getDoc, runTransaction, Timestamp } from "firebase/firestore";
import { paths } from "./paths";
import { asDate, parseDiscovery } from "./codec";
import { cleanForFirestore } from "./serialize";
import {
  validateDiscoveryModel,
  validateJourney,
  type PublishedDiscoveryModel,
} from "@/domain/discovery-model";
import type { Discovery, DiscoverySessionContext } from "@/domain/types";
import { resolveDiscoveryPath } from "@/domain/discovery-navigation";
import { resumeJourney } from "@/discovery/journey-state";

const cache = new Map<string, PublishedDiscoveryModel>();
export function parsePublishedModel(
  id: string,
  raw: Record<string, unknown>,
): PublishedDiscoveryModel {
  if (
    !id ||
    raw["schemaVersion"] !== 1 ||
    raw["runtimeVersion"] !== 1 ||
    (raw["contextId"] !== undefined &&
      (typeof raw["contextId"] !== "string" ||
        !raw["contextId"] ||
        raw["contextId"] === "main" ||
        raw["contextId"].includes("/"))) ||
    (raw["versionNumber"] !== undefined &&
      (!Number.isInteger(raw["versionNumber"]) || Number(raw["versionNumber"]) < 1)) ||
    !Number.isInteger(raw["publishedAtRevision"]) ||
    Number(raw["publishedAtRevision"]) < 0
  )
    throw new Error("Unsupported or invalid published Discovery model.");
  return {
    id,
    ...(typeof raw["contextId"] === "string" ? { contextId: raw["contextId"] } : {}),
    ...(raw["versionNumber"] !== undefined
      ? { versionNumber: raw["versionNumber"] as number }
      : {}),
    schemaVersion: 1,
    runtimeVersion: 1,
    publishedAt: asDate(raw["publishedAt"]),
    publishedAtRevision: raw["publishedAtRevision"] as number,
    model: validateDiscoveryModel(raw["model"]),
  };
}
export async function getPublishedModel(
  customerId: string,
  modelId: string,
): Promise<PublishedDiscoveryModel> {
  const key = `${customerId}/${modelId}`;
  const existing = cache.get(key);
  if (existing) return structuredClone(existing);
  const snapshot = await getDoc(paths.discoveryModel(customerId, modelId));
  if (!snapshot.exists())
    throw new Error(
      "The original Discovery model is unavailable. Please retry; this journey cannot use a different model.",
    );
  const model = parsePublishedModel(modelId, snapshot.data());
  cache.set(key, model);
  return structuredClone(model);
}
export async function getLiveModel(customerId: string): Promise<PublishedDiscoveryModel> {
  const pointer = await getDoc(paths.discoveryLive(customerId));
  const id = pointer.data()?.["modelId"];
  if (typeof id !== "string" || !id)
    throw new Error("This company needs Discovery model migration before new journeys can start.");
  return getPublishedModel(customerId, id);
}
export function initialDiscovery(
  model: PublishedDiscoveryModel,
  context: DiscoverySessionContext,
  authenticatedUserId?: string | null,
): Discovery {
  const now = new Date();
  const start = resolveDiscoveryPath(model.model, {}).path[0]!;
  return {
    id: context.sessionId,
    sessionId: context.sessionId,
    context,
    ...(authenticatedUserId ? { authenticatedUserId } : {}),
    schemaVersion: 2,
    modelId: model.id,
    scenarioId: model.model.scenario.id,
    businessRevision: model.publishedAtRevision,
    status: "in_progress",
    stage: "questions",
    currentStepId: start.stepId,
    currentQuestionId: start.question.id,
    questionIndex: start.questionIndex,
    answers: {},
    visitorNotes: "",
    presentedQuestions: [],
    result: null,
    startedAt: now,
    firstAnswerAt: null,
    completedAt: null,
    contactRequestedAt: null,
    updatedAt: now,
    expiresAt: new Date(now.getTime() + 86_400_000),
  };
}

/** Binding is selected inside the transaction, never from the intro's cache. */
export async function startPublishedDiscovery(
  customerId: string,
  context: DiscoverySessionContext,
  authenticatedUserId?: string | null,
): Promise<{ discovery: Discovery; model: PublishedDiscoveryModel }> {
  return runTransaction(paths.customer(customerId).firestore, async (tx) => {
    const reference = paths.discovery(customerId, context.sessionId);
    const prior = await tx.get(reference);
    let discovery = prior.exists() ? parseDiscovery(prior.id, prior.data()) : null;
    if (discovery && discovery.context.mode !== context.mode)
      throw new Error("Discovery mode cannot change. Start a fresh journey.");
    let modelId = discovery?.modelId;
    if (discovery && !modelId)
      throw new Error("This is a legacy journey. Start a new journey to continue.");
    if (!discovery) {
      const live = await tx.get(paths.discoveryLive(customerId));
      modelId = live.data()?.["modelId"] as string | undefined;
    }
    if (!modelId) throw new Error("No published Discovery model is available.");
    const snapshot = await tx.get(paths.discoveryModel(customerId, modelId));
    if (!snapshot.exists()) throw new Error("The pinned Discovery model is missing.");
    const model = parsePublishedModel(modelId, snapshot.data());
    if (discovery) {
      discovery = resumeJourney(model.model, discovery);
      validateJourney(model.model, discovery);
    } else {
      discovery = initialDiscovery(model, context, authenticatedUserId);
      const { id: _id, ...data } = discovery;
      tx.set(
        reference,
        cleanForFirestore({
          ...data,
          startedAt: Timestamp.fromDate(discovery.startedAt),
          updatedAt: Timestamp.fromDate(discovery.updatedAt),
          expiresAt: Timestamp.fromDate(discovery.expiresAt),
        }),
      );
    }
    return { discovery, model };
  });
}
